import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, WarningCircle } from 'phosphor-react';
import {
  ClassificacaoFidelidade,
  IAuditoriaFidelidade,
  IClausulaAuditada,
  IItemParecer,
  IParecerValidacao,
  IVeredictoFidelidade,
  StatusParecer,
  useAuditFidelidade,
} from '../../../../hooks/useAuditFidelidade';

// O motor novo manda os itens "Conferir" como suspeita da cláusula 0: sem bloco próprio eles
// apareciam como "defeito da geração" (E2E de 25/09).
const ehConferir = (v: { motivo?: string | null }) => (v.motivo ?? '').startsWith('Conferir:');

// "a, b e c" (vazio sem itens).
const juntarComE = (itens: string[]): string =>
  itens.length > 1
    ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`
    : itens.join('');

const COR_CLASSIF: Record<ClassificacaoFidelidade, string> = {
  legitima: 'green',
  suspeita: 'red',
  vestigial: 'orange',
};

const LABEL_CLASSIF: Record<ClassificacaoFidelidade, string> = {
  legitima: 'Legítima',
  suspeita: 'Suspeita',
  vestigial: 'Vestigial',
};

// Ordem de atencao: suspeita primeiro, depois vestigial, por fim legitima.
const PESO_CLASSIF: Record<ClassificacaoFidelidade, number> = {
  suspeita: 0,
  vestigial: 1,
  legitima: 2,
};

const CTX = 50;

// Isola o trecho que mudou: prefixo/sufixo comum (com janela) + o meio divergente.
function diffSpan(a: string, b: string) {
  let i = 0;
  const m = Math.min(a.length, b.length);
  while (i < m && a[i] === b[i]) i += 1;
  let j = 0;
  while (j < m - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j += 1;
  // Encaixa nas BORDAS DE PALAVRA: recua o início até depois do último espaço e
  // estende o fim até o próximo espaço — destaca palavras INTEIRAS (ex.: "Os
  // sócios" em vez de "s sócios"). O char de borda pertence ao prefixo/sufixo
  // COMUM, então o mesmo ajuste vale pros dois textos.
  const isSpace = (c: string | undefined) => !!c && /\s/.test(c);
  while (i > 0 && !isSpace(a[i - 1])) i -= 1;
  while (j > 0 && !isSpace(a[a.length - j])) j -= 1;
  const preStart = Math.max(0, i - CTX);
  const pre = (preStart > 0 ? '…' : '') + a.slice(preStart, i);
  const posEndA = Math.min(a.length, a.length - j + CTX);
  const pos = a.slice(a.length - j, posEndA) + (posEndA < a.length ? '…' : '');
  return {
    pre,
    vigMid: a.slice(i, a.length - j),
    renMid: b.slice(i, b.length - j),
    pos,
  };
}

const CompText = ({
  pre,
  mid,
  pos,
  midBg,
  midColor,
}: {
  pre: string;
  mid: string;
  pos: string;
  midBg: string;
  midColor: string;
}) => (
  <Text fontSize="xs" lineHeight="1.6" color="#475569" whiteSpace="pre-wrap">
    {pre}
    {mid ? (
      <Box as="mark" bg={midBg} color={midColor} px="2px" borderRadius="sm" fontWeight="600">
        {mid}
      </Box>
    ) : null}
    {pos}
  </Text>
);

// O juiz CITA o trecho ofensor entre aspas simples no motivo (ex.: Texto 'Junta
// Comercial do Estado de Minas Gerais' aparece como lixo/artefato...). Dá pra
// usar isso pra mostrar só a VIZINHANÇA do problema, em vez de despejar a
// cláusula inteira — o operador reclamou de "porrada de texto" (2026-08-11).
const RX_CITADO = /'([^']{4,140})'/;

const trechoCitado = (motivo: string): string | null => {
  const m = motivo?.match(RX_CITADO);
  return m ? m[1] : null;
};

const CTX_CITADO = 80;

// Janela em volta do trecho citado. `null` quando a citação não é encontrada
// literalmente no render (juiz parafraseou, ou o texto tem quebra no meio) —
// aí o card cai no fallback de excerto curto.
function janelaEmVolta(texto: string, alvo: string) {
  const i = texto.indexOf(alvo);
  if (i < 0) return null;
  const ini = Math.max(0, i - CTX_CITADO);
  const fim = Math.min(texto.length, i + alvo.length + CTX_CITADO);
  return {
    pre: (ini > 0 ? '…' : '') + texto.slice(ini, i),
    mid: alvo,
    pos: texto.slice(i + alvo.length, fim) + (fim < texto.length ? '…' : ''),
  };
}

const VeredictoRow = ({ v }: { v: IVeredictoFidelidade }) => {
  const cor = ehConferir(v) ? 'yellow' : (COR_CLASSIF[v.classificacao] ?? 'gray');
  const temVig = !!v.texto_vigente;
  const temRen = !!v.texto_render;
  const { pre, vigMid, renMid, pos } = temVig
    ? diffSpan(v.texto_vigente || '', v.texto_render || '')
    : { pre: '', vigMid: '', renMid: '', pos: '' };

  return (
    <Box borderWidth="1px" borderColor="rgba(15,23,42,0.08)" borderRadius="lg" p={4}>
      <Flex align="center" gap={2} mb={2} wrap="wrap">
        <Badge colorScheme={cor}>
          {ehConferir(v) ? 'Conferir' : (LABEL_CLASSIF[v.classificacao] ?? v.classificacao)}
        </Badge>
        <Text fontSize="sm" fontWeight="700" color="#0F172A">
          {v.clausula_numero > 0 ? `Cláusula ${v.clausula_numero}` : 'Varredura geral'}
        </Text>
        {v.motivo ? (
          <Text fontSize="xs" color="#64748B">
            — {v.motivo}
          </Text>
        ) : null}
      </Flex>

      {/* Fidelidade (vigente × render): duas colunas com o trecho que mudou destacado. */}
      {temVig ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Box bg="#F8FAFC" borderRadius="md" p={3}>
            <Text fontSize="10px" textTransform="uppercase" color="#94A3B8" fontWeight="700" mb={1}>
              Vigente
            </Text>
            <CompText pre={pre} mid={vigMid} pos={pos} midBg="#FEE2E2" midColor="#B91C1C" />
          </Box>
          <Box bg="#FFF7ED" borderRadius="md" p={3}>
            <Text fontSize="10px" textTransform="uppercase" color="#94A3B8" fontWeight="700" mb={1}>
              Gerado (render)
            </Text>
            <CompText pre={pre} mid={renMid} pos={pos} midBg="#FFEDD5" midColor="#C2410C" />
          </Box>
        </SimpleGrid>
      ) : null}

      {/* Achado HOLÍSTICO (formulário × render, sem contraparte no vigente).
          Mostra a VIZINHANÇA do trecho que o juiz citou, com ele destacado —
          antes despejava 600 chars da cláusula e o operador tinha que caçar o
          problema no meio. Sem citação localizável, cai no excerto curto. */}
      {!temVig && temRen ? <TrechoDoAchado motivo={v.motivo} render={v.texto_render} /> : null}
    </Box>
  );
};

// Agrupa achados que citam o MESMO trecho — uma causa raiz aparecendo em N
// lugares. No contrato 125, 'Junta Comercial do Estado de Minas Gerais' vaza no
// preâmbulo e nas cláusulas 5 e 6: três cards quase idênticos que o operador lê
// três vezes pra descobrir que é um problema só.
type GrupoAchado = { chave: string; itens: IVeredictoFidelidade[] };

const agruparPorCausa = (vs: IVeredictoFidelidade[]): GrupoAchado[] => {
  const mapa = new Map<string, IVeredictoFidelidade[]>();
  vs.forEach(v => {
    const citado = trechoCitado(v.motivo || '');
    // Sem citação não dá pra afirmar causa comum — chave única, não agrupa.
    const chave = citado ?? `unico::${v.clausula_numero}::${(v.motivo || '').slice(0, 60)}`;
    mapa.set(chave, [...(mapa.get(chave) ?? []), v]);
  });
  return Array.from(mapa.entries()).map(([chave, itens]) => ({ chave, itens }));
};

const ondeFica = (v: IVeredictoFidelidade) =>
  v.clausula_numero > 0 ? `Cláusula ${v.clausula_numero}` : 'Preâmbulo';

// N ocorrências da mesma causa num card só. 1 ocorrência = card normal.
const AchadoAgrupado = ({ grupo }: { grupo: GrupoAchado }) => {
  const [tudo, setTudo] = useState(false);
  if (grupo.itens.length === 1) return <VeredictoRow v={grupo.itens[0]} />;

  const primeiro = grupo.itens[0];
  const cor = ehConferir(primeiro) ? 'yellow' : (COR_CLASSIF[primeiro.classificacao] ?? 'gray');
  const visiveis = tudo ? grupo.itens : grupo.itens.slice(0, 1);

  return (
    <Box borderWidth="1px" borderColor="rgba(15,23,42,0.08)" borderRadius="lg" p={4}>
      <Flex align="center" gap={2} mb={1} wrap="wrap">
        <Badge colorScheme={cor}>
          {ehConferir(primeiro)
            ? 'Conferir'
            : (LABEL_CLASSIF[primeiro.classificacao] ?? primeiro.classificacao)}
        </Badge>
        <Badge colorScheme="purple" variant="subtle">
          {grupo.itens.length} ocorrências
        </Badge>
        <Text fontSize="sm" fontWeight="700" color="#0F172A">
          {grupo.itens.map(ondeFica).join(' · ')}
        </Text>
      </Flex>
      <Text fontSize="xs" color="#64748B" mb={2}>
        — {primeiro.motivo}
      </Text>
      <VStack align="stretch" spacing={2}>
        {visiveis.map(v => (
          <Box key={`${v.clausula_numero}-${v.motivo?.slice(0, 24)}`}>
            <Text fontSize="10px" color="#94A3B8" fontWeight="700" mb={1}>
              {ondeFica(v)}
            </Text>
            {/* Citação igual não garante motivo igual. Quando o juiz escreveu
                algo diferente pra esta ocorrência, mostra — senão agrupar
                engoliria o achado distinto. */}
            {v.motivo && v.motivo !== primeiro.motivo ? (
              <Text fontSize="xs" color="#64748B" mb={1}>
                — {v.motivo}
              </Text>
            ) : null}
            <TrechoDoAchado motivo={v.motivo} render={v.texto_render} />
          </Box>
        ))}
      </VStack>
      <Text
        as="button"
        fontSize="10px"
        fontWeight="600"
        color="#7C3AED"
        mt={2}
        onClick={() => setTudo(x => !x)}
      >
        {tudo
          ? '▾ mostrar só a primeira ocorrência'
          : `▸ ver as outras ${grupo.itens.length - 1} ocorrências`}
      </Text>
    </Box>
  );
};

// Os dois blocos existem porque a AÇÃO é diferente — e o back já diz isso no
// schema: vestigial é "sinal pra remoção formal (produto/jurídico), NÃO defeito
// de render"; suspeita é infidelidade textual do render.
//
// Antes eu ordenava por gravidade (PESO_CLASSIF: suspeita antes de vestigial) e
// jogava tudo numa lista só. Resultado no contrato 125: o único item que exige
// ação do operador ANTES de protocolar na Junta ficava embaixo de 5 suspeitas,
// três das quais eram a mesma causa. Bloco separado não afunda, não importa
// quantas suspeitas apareçam.
const BLOCOS_ACAO: {
  chave: string;
  pertence: (v: any) => boolean;
  badge: string;
  titulo: string;
  sub: string;
  cor: string;
  bg: string;
  borda: string;
}[] = [
  {
    chave: 'conferir',
    pertence: v => v.classificacao === 'suspeita' && ehConferir(v),
    badge: 'yellow',
    titulo: 'Para conferir antes de liberar',
    sub: 'Pontos que o gerador pediu para você olhar no documento. Se estiver certo, libere; se não, ajuste à mão.',
    cor: '#A16207',
    bg: '#FEFCE8',
    borda: '#FDE047',
  },
  {
    chave: 'vestigial',
    pertence: v => v.classificacao === 'vestigial',
    badge: 'orange',
    titulo: 'Ação do operador antes de enviar à Junta',
    sub: 'Texto fiel ao vigente, mas que perdeu sentido no novo quadro. A remoção é decisão jurídica — o gerador preserva e não pode decidir apagar.',
    cor: '#C2410C',
    bg: '#FFF7ED',
    borda: '#FDBA74',
  },
  {
    chave: 'suspeita',
    pertence: v => v.classificacao === 'suspeita' && !ehConferir(v),
    badge: 'red',
    titulo: 'Possível defeito da geração',
    sub: 'Infidelidade textual do render. Editar o documento não resolve a causa — precisa correção no gerador.',
    cor: '#B91C1C',
    bg: '#FEF2F2',
    borda: '#FCA5A5',
  },
];

const EXCERTO_CURTO = 240;

const TrechoDoAchado = ({ motivo, render }: { motivo: string; render: string }) => {
  const [aberto, setAberto] = useState(false);
  const citado = trechoCitado(motivo || '');
  const janela = citado ? janelaEmVolta(render, citado) : null;

  return (
    <Box bg="#FFF7ED" borderRadius="md" p={3}>
      <Flex align="center" justify="space-between" gap={2} mb={1}>
        <Text fontSize="10px" textTransform="uppercase" color="#94A3B8" fontWeight="700">
          {janela ? 'Onde aparece' : 'Trecho no gerado (render)'}
        </Text>
        {render.length > EXCERTO_CURTO ? (
          <Text
            as="button"
            fontSize="10px"
            fontWeight="600"
            color="#C2410C"
            onClick={() => setAberto(x => !x)}
          >
            {aberto ? '▾ menos' : '▸ ver cláusula inteira'}
          </Text>
        ) : null}
      </Flex>
      {aberto || !janela ? (
        <Text fontSize="xs" lineHeight="1.6" color="#475569" whiteSpace="pre-wrap">
          {aberto || render.length <= EXCERTO_CURTO ? render : `${render.slice(0, EXCERTO_CURTO)}…`}
        </Text>
      ) : (
        <CompText
          pre={janela.pre}
          mid={janela.mid}
          pos={janela.pos}
          midBg="#FFEDD5"
          midColor="#C2410C"
        />
      )}
    </Box>
  );
};

// Camada 1 F2 — mapa completo: origem (de onde veio o texto) + status de fidelidade.
const ORIGEM_META: Record<string, { label: string; color: string; dot: string }> = {
  preservada: { label: 'do vigente', color: 'blue', dot: '#3B82F6' },
  substituida: { label: 'campo alterado', color: 'orange', dot: '#F59E0B' },
  gerada: { label: 'gerada', color: 'green', dot: '#22C55E' },
  corpo: { label: 'gerada', color: 'green', dot: '#22C55E' },
};
// Só os status que `visualDaClausula` NÃO trata antes (ela decide `divergente` e
// `nova` olhando motivo/origem). Deixar 'nova — revise' aqui fazia parecer que o
// rótulo ainda aparece; não aparece mais. Status desconhecido cai no `??`.
const STATUS_META: Record<string, { label: string; color: string }> = {
  identica: { label: 'idêntica', color: 'green' },
  assinatura: { label: 'assinatura', color: 'gray' },
};
const PARTE_LABEL: Record<string, string> = {
  A: 'Parte A — Alterações',
  B: 'Parte B — Contrato consolidado',
  constituicao: 'Corpo',
};

// Rótulo humano da FAMÍLIA da regra. O back manda `regra_padrao` (ex.:
// `uni_para_pluri_genitivo`, `pluri_uni:os sócios`, `din_artigo`, `pol_...`) —
// bom pra auditar, ilegível pro operador. Aqui vira uma frase.
const familiaDaRegra = (r: string): string => {
  if (r.startsWith('pluri_uni:')) return 'plural → singular';
  if (r.startsWith('uni_para_pluri')) return 'singular → plural';
  if (r.startsWith('din_')) return 'gênero/número do quadro';
  if (r.startsWith('pol_')) return 'ajuste de concordância';
  return 'regra registrada';
};

const resumoDasRegras = (regras?: string[]): string => {
  if (!regras?.length) return '';
  const familias = Array.from(new Set(regras.map(familiaDaRegra)));
  return familias.join(' · ');
};

// Status COMO O OPERADOR PRECISA VER — não o enum cru.
//
// Queixa (2026-08-11): *"o mapa sempre vem com divergências... cláusulas que nem
// se alteram vêm marcadas como alteradas"*. O back passou a dizer POR QUE cada
// uma divergiu; aqui isso vira hierarquia visual. Só sobra em vermelho o que o
// operador precisa mesmo olhar: divergência de conteúdo (`motivo` nulo).
// `label: null` = não mostra badge de status (o de origem já disse tudo).
type VisualClausula = {
  label: string | null;
  color: string;
  solid: boolean;
  atencao: boolean;
};

const visualDaClausula = (c: IClausulaAuditada): VisualClausula => {
  if (c.status === 'divergente') {
    if (c.motivo_divergencia === 'concordancia') {
      return { label: 'concordância', color: 'blue', solid: false, atencao: false };
    }
    if (c.motivo_divergencia === 'formatacao') {
      return { label: 'formatação', color: 'gray', solid: false, atencao: false };
    }
    return { label: 'revisar', color: 'red', solid: true, atencao: true };
  }
  if (c.status === 'nova') {
    // `nova` significa coisas MUITO diferentes conforme a origem:
    //
    //   gerada/substituida/corpo -> texto novo por construção. TODA alteração
    //     tem cláusula gerada na Parte A e campo atualizado na Parte B. Alarmar
    //     nisso faz o contador nunca zerar — no contrato 125 dava "3 para
    //     revisar" com ZERO divergência. E o badge repetia o de origem
    //     ("GERADA" + "NOVA — REVISE"). Fora: sem badge de status.
    //   preservada -> cláusula marcada como vinda do vigente mas SEM contraparte
    //     lá (`orig_txt` vazio no back). Esta sim merece olho.
    if (c.origem === 'preservada') {
      return { label: 'sem par no vigente', color: 'purple', solid: true, atencao: true };
    }
    return { label: null, color: 'gray', solid: false, atencao: false };
  }
  const sm = STATUS_META[c.status] ?? { label: c.status, color: 'gray' };
  return { label: sm.label, color: sm.color, solid: false, atencao: false };
};

const ClausulaRow = ({ c }: { c: IClausulaAuditada }) => {
  const om = ORIGEM_META[c.origem] ?? { label: c.origem, color: 'gray', dot: '#94A3B8' };
  const sm = visualDaClausula(c);
  const regras = resumoDasRegras(c.regras_concordancia);
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={2}
      py={1.5}
      px={3}
      borderBottomWidth="1px"
      borderColor="rgba(15,23,42,0.05)"
    >
      <Flex align="center" gap={2} minW={0}>
        <Box w="8px" h="8px" borderRadius="full" bg={om.dot} flexShrink={0} />
        <Text fontSize="xs" color="#0F172A" fontWeight="600" flexShrink={0}>
          Cl. {c.numero}
        </Text>
        <Text fontSize="xs" color="#64748B" noOfLines={1}>
          {c.titulo}
        </Text>
      </Flex>
      <Flex align="center" gap={2} flexShrink={0}>
        {/* Regra que explica — some quando não há (o caso 'revisar' puro). */}
        {regras ? (
          <Text
            fontSize="10px"
            color="#94A3B8"
            noOfLines={1}
            display={{ base: 'none', md: 'block' }}
          >
            {regras}
          </Text>
        ) : null}
        <Badge colorScheme={om.color} variant="subtle" fontSize="9px">
          {om.label}
        </Badge>
        {sm.label ? (
          <Badge colorScheme={sm.color} variant={sm.solid ? 'solid' : 'subtle'} fontSize="9px">
            {sm.label}
          </Badge>
        ) : null}
      </Flex>
    </Flex>
  );
};

const MapaClausulas = ({ clausulas }: { clausulas: IClausulaAuditada[] }) => {
  // Filtro do operador: o mapa completo serve pra conferir origem de tudo, mas
  // no dia a dia ele quer ir direto no que precisa de olho. Default = tudo
  // visível (não esconder informação sem o operador pedir).
  const [soAtencao, setSoAtencao] = useState(false);

  const nAtencao = clausulas.filter(c => visualDaClausula(c).atencao).length;
  const nConcordancia = clausulas.filter(c => c.motivo_divergencia === 'concordancia').length;
  const nFormatacao = clausulas.filter(c => c.motivo_divergencia === 'formatacao').length;
  const nGeradas = clausulas.filter(c => c.status === 'nova' && c.origem !== 'preservada').length;
  const nIdenticas = clausulas.filter(c => c.status === 'identica' && !c.motivo_divergencia).length;

  // Decomposição em UMA linha discreta, só o que não é zero — em vez de uma
  // fileira de badges competindo com o veredito.
  const decomposicao = [
    nConcordancia ? `${nConcordancia} concordância` : '',
    nFormatacao ? `${nFormatacao} formatação` : '',
    nIdenticas ? `${nIdenticas} idêntica${nIdenticas > 1 ? 's' : ''} ao vigente` : '',
    nGeradas ? `${nGeradas} gerada${nGeradas > 1 ? 's' : ''} pelo ato` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const visiveis = soAtencao ? clausulas.filter(c => visualDaClausula(c).atencao) : clausulas;
  const partes = ['A', 'B', 'constituicao'].filter(p => visiveis.some(c => c.parte === p));
  const semParte = visiveis.filter(c => !c.parte || !['A', 'B', 'constituicao'].includes(c.parte));

  return (
    <Box borderWidth="1px" borderColor="rgba(15,23,42,0.08)" borderRadius="lg" overflow="hidden">
      <Flex align="center" gap={2} px={3} py={2} bg="#F8FAFC" wrap="wrap">
        <Text fontSize="xs" fontWeight="700" color="#0F172A">
          Mapa completo — {clausulas.length} cláusulas
        </Text>
        <Badge colorScheme={nAtencao ? 'red' : 'green'} variant="solid" fontSize="9px">
          {nAtencao ? `${nAtencao} para revisar` : 'nada para revisar'}
        </Badge>
        <Box flex="1" />
        {nAtencao ? (
          <Button
            size="xs"
            variant={soAtencao ? 'solid' : 'outline'}
            colorScheme="red"
            height="22px"
            fontSize="10px"
            onClick={() => setSoAtencao(v => !v)}
          >
            {soAtencao ? 'Ver todas' : 'Só o que precisa de atenção'}
          </Button>
        ) : null}
      </Flex>
      {decomposicao ? (
        <Flex px={3} pb={2} bg="#F8FAFC">
          <Text fontSize="10px" color="#94A3B8">
            {decomposicao}
            {nConcordancia
              ? ' — concordância é a alteração acontecendo (sócio entrou/saiu), não infidelidade.'
              : ''}
          </Text>
        </Flex>
      ) : null}
      {partes.map(p => (
        <Box key={p}>
          <Text
            fontSize="10px"
            fontWeight="700"
            color="#94A3B8"
            textTransform="uppercase"
            px={3}
            pt={2}
            pb={1}
          >
            {PARTE_LABEL[p] ?? p}
          </Text>
          {visiveis
            .filter(c => c.parte === p)
            .map(c => (
              <ClausulaRow key={`${p}-${c.numero}`} c={c} />
            ))}
        </Box>
      ))}
      {semParte.map(c => (
        <ClausulaRow key={`x-${c.numero}`} c={c} />
      ))}
    </Box>
  );
};

// === Fase 2 (issue #1158): parecer da validação (síntese formato-issue) =======
// Reagrupa (no back, determinístico) o julgamento que o auditor já fez nos 6
// grupos que a issue pede + status. Aqui só exibe: cabeçalho executivo acima do
// detalhamento técnico (vereditos + mapa) que já existia.
const STATUS_PARECER: Record<
  StatusParecer,
  { label: string; color: string; bg: string; border: string }
> = {
  aprovado: { label: 'Aprovado', color: '#15803D', bg: '#F0FDF4', border: '#86EFAC' },
  necessita_revisao: {
    label: 'Necessita revisão',
    color: '#C2410C',
    bg: '#FFF7ED',
    border: '#FDBA74',
  },
};

type BucketParecerKey =
  | 'alteracoes'
  | 'adicionadas'
  | 'removidas'
  | 'inconsistencias'
  | 'conferir'
  | 'pendencias';

const BUCKETS_PARECER: { key: BucketParecerKey; label: string; dot: string }[] = [
  { key: 'alteracoes', label: 'Alterações', dot: '#F59E0B' },
  { key: 'adicionadas', label: 'Adicionadas', dot: '#22C55E' },
  { key: 'removidas', label: 'Removidas', dot: '#EF4444' },
  { key: 'conferir', label: 'Para conferir', dot: '#CA8A04' },
  { key: 'inconsistencias', label: 'Inconsistências', dot: '#DC2626' },
  { key: 'pendencias', label: 'Pendências', dot: '#F97316' },
];

const ItemParecerLinha = ({ it }: { it: IItemParecer }) => (
  <Text fontSize="11px" color="#475569" noOfLines={2} lineHeight="1.5">
    <Text as="span" fontWeight="700" color="#0F172A">
      {it.clausula_numero > 0 ? `Cl. ${it.clausula_numero}` : 'Geral'}
      {it.parte ? ` (${it.parte})` : ''}
    </Text>
    {it.descricao ? ` — ${it.descricao}` : ''}
    {it.titulo ? ` · ${it.titulo}` : ''}
  </Text>
);

const BucketParecer = ({
  label,
  dot,
  itens,
}: {
  label: string;
  dot: string;
  itens: IItemParecer[];
}) => (
  <Box
    bg="rgba(255,255,255,0.65)"
    borderWidth="1px"
    borderColor="rgba(15,23,42,0.07)"
    borderRadius="md"
    p={3}
  >
    <Flex align="center" gap={2} mb={1.5}>
      <Box w="8px" h="8px" borderRadius="full" bg={dot} flexShrink={0} />
      <Text fontSize="xs" fontWeight="700" color="#0F172A">
        {label}
      </Text>
      <Badge colorScheme="gray" variant="subtle" fontSize="9px">
        {itens.length}
      </Badge>
    </Flex>
    <VStack align="stretch" spacing={1} pl={3.5}>
      {itens.map(it => (
        <ItemParecerLinha key={`${it.clausula_numero}-${it.descricao}`} it={it} />
      ))}
    </VStack>
  </Box>
);

const ParecerValidacaoBlock = ({ parecer }: { parecer: IParecerValidacao }) => {
  const sm = STATUS_PARECER[parecer.status] ?? STATUS_PARECER.necessita_revisao;
  const aprovado = parecer.status === 'aprovado';
  // "Conferir: ..." chega em inconsistencias (suspeita da cláusula 0); aqui vira grupo próprio.
  const todas = parecer.inconsistencias ?? [];
  const porGrupo: Record<BucketParecerKey, IItemParecer[]> = {
    alteracoes: parecer.alteracoes ?? [],
    adicionadas: parecer.adicionadas ?? [],
    removidas: parecer.removidas ?? [],
    conferir: todas.filter(it => ehConferir({ motivo: it.descricao })),
    inconsistencias: todas.filter(it => !ehConferir({ motivo: it.descricao })),
    pendencias: parecer.pendencias ?? [],
  };
  const buckets = BUCKETS_PARECER.map(b => ({ ...b, itens: porGrupo[b.key] })).filter(
    b => b.itens.length > 0,
  );
  return (
    <Box borderWidth="1px" borderColor={sm.border} bg={sm.bg} borderRadius="xl" p={4}>
      <Flex align="center" gap={2} mb={2} wrap="wrap">
        {aprovado ? (
          <CheckCircle size={22} color={sm.color} weight="fill" />
        ) : (
          <WarningCircle size={22} color={sm.color} weight="fill" />
        )}
        <Text
          fontSize="sm"
          fontWeight="800"
          color={sm.color}
          textTransform="uppercase"
          letterSpacing="0.02em"
        >
          {sm.label}
        </Text>
        <Text fontSize="xs" color="#64748B">
          — parecer da validação
        </Text>
      </Flex>
      <Text fontSize="sm" color="#334155" mb={buckets.length ? 3 : 0} lineHeight="1.6">
        {parecer.justificativa}
      </Text>
      {buckets.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
          {buckets.map(b => (
            <BucketParecer key={b.key} label={b.label} dot={b.dot} itens={b.itens} />
          ))}
        </SimpleGrid>
      ) : null}
    </Box>
  );
};

interface Props {
  auditoria: IAuditoriaFidelidade;
  // Fase 1 gate: com `contratoId` presente, mostra o CTA de confirmação da
  // vistoria (Conferi → libera download). Ausente ⇒ relatório read-only.
  contratoId?: number;
  // Este relatório (auditoria.id) JÁ foi confirmado no contrato.
  revisada?: boolean;
  // Chamado após confirmar com sucesso (re-busca o download / invalida a lista).
  onRevisado?: () => void;
}

export const RelatorioFidelidade = ({ auditoria, contratoId, revisada, onRevisado }: Props) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { confirmarAuditoria } = useAuditFidelidade();
  const [confirmadoLocal, setConfirmadoLocal] = useState(false);
  const [mostrarLegitimas, setMostrarLegitimas] = useState(false);
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const jaRevisada = !!revisada || confirmadoLocal;
  // Os Conferir vem como suspeita: fora do contador, que e' de defeito da geracao.
  const nConferir = (auditoria.vereditos ?? []).filter(
    v => v.classificacao === 'suspeita' && ehConferir(v),
  ).length;
  const nSuspeitas = Math.max(0, auditoria.total_suspeitas - nConferir);
  // O texto da confirmacao separa os tres grupos, como o painel (review do PR #772).
  const pontos = juntarComE(
    [
      nConferir > 0 ? `${nConferir} para conferir` : '',
      nSuspeitas > 0 ? `${nSuspeitas} suspeita(s)` : '',
      auditoria.total_vestigiais > 0 ? `${auditoria.total_vestigiais} vestigial(is)` : '',
    ].filter(Boolean),
  );
  const conferiu = pontos ? `os pontos de atenção (${pontos})` : 'a vistoria';

  const confirmar = useMutation({
    mutationFn: () => confirmarAuditoria(contratoId as number, auditoria.id),
    onSuccess: () => {
      setConfirmadoLocal(true);
      toast({ status: 'success', title: 'Vistoria revisada — download liberado.' });
      onRevisado?.();
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      // 409 RELATORIO_DESATUALIZADO: surgiu uma vistoria mais nova (ex.: alguém
      // regerou) entre abrir o painel e clicar confirmar. Invalida as DUAS
      // queries que montam este relatório (Drawer + pós-geração) pra puxar a
      // vistoria mais recente — senão o operador fica preso no relatório velho
      // e reclica em 409 sem saída além de recarregar a página na mão.
      if (
        typeof detail === 'string' &&
        detail.startsWith('RELATORIO_DESATUALIZADO') &&
        contratoId != null
      ) {
        queryClient.invalidateQueries({ queryKey: ['auditoria-fidelidade-contrato', contratoId] });
        queryClient.invalidateQueries({ queryKey: ['validacao-fidelidade-posgen', contratoId] });
      }
      toast({
        status: 'error',
        title: 'Não foi possível confirmar a vistoria',
        description: typeof detail === 'string' ? detail : undefined,
      });
    },
  });

  const vereditos = [...(auditoria.vereditos ?? [])].sort(
    (a, b) => (PESO_CLASSIF[a.classificacao] ?? 9) - (PESO_CLASSIF[b.classificacao] ?? 9),
  );
  // Foco ("revisão de 30 s"): suspeitas/vestigiais expandidos; legítimas
  // colapsadas atrás de um toggle.
  const atencao = vereditos.filter(v => v.classificacao !== 'legitima');
  const legitimas = vereditos.filter(v => v.classificacao === 'legitima');

  return (
    <VStack align="stretch" spacing={4}>
      {/* Fase 2 (issue #1158): parecer executivo no topo; o detalhamento técnico
          (stats + vereditos + mapa) segue abaixo. Só aparece no endpoint por
          contrato (on-read) — no feed agregado `parecer` vem ausente. */}
      {auditoria.parecer ? <ParecerValidacaoBlock parecer={auditoria.parecer} /> : null}

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Stat>
          <StatLabel>Preservadas</StatLabel>
          <StatNumber>{auditoria.total_clausulas_preservadas}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Divergências</StatLabel>
          <StatNumber>{auditoria.total_divergencias}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel color="red.600">Suspeitas</StatLabel>
          <StatNumber color="red.600">{nSuspeitas}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel color="orange.600">Vestigiais</StatLabel>
          <StatNumber color="orange.600">{auditoria.total_vestigiais}</StatNumber>
        </Stat>
      </SimpleGrid>

      {auditoria.erro ? (
        <Text fontSize="xs" color="red.500">
          Auditor fail-open: {auditoria.erro}
        </Text>
      ) : null}

      {(auditoria.estrutura?.clausulas_sumidas?.length ?? 0) > 0 ? (
        <Box borderWidth="1px" borderColor="#FCA5A5" bg="#FEF2F2" borderRadius="lg" p={3}>
          <Text fontSize="sm" fontWeight="700" color="#B91C1C">
            ⚠️ Cláusula(s) do vigente ausente(s) no gerado:{' '}
            {auditoria.estrutura?.clausulas_sumidas.join(', ')}
          </Text>
          <Text fontSize="xs" color="#7F1D1D">
            Confirme se a remoção foi intencional antes de liberar o download.
          </Text>
        </Box>
      ) : null}

      {vereditos.length === 0 ? (
        <Text fontSize="sm" color="#71717A">
          Sem divergências julgadas — cláusulas preservadas byte-fiel.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {atencao.length === 0 ? (
            <Text fontSize="sm" fontWeight="600" color="#0F172A">
              Nenhum ponto de atenção — {legitimas.length} divergência(s) legítima(s) conferida(s)
              pela IA.
            </Text>
          ) : null}

          {BLOCOS_ACAO.map(bl => {
            const doBloco = atencao.filter(bl.pertence);
            if (doBloco.length === 0) return null;
            const grupos = agruparPorCausa(doBloco);
            return (
              <Box
                key={bl.chave}
                borderWidth="1px"
                borderColor={bl.borda}
                bg={bl.bg}
                borderRadius="lg"
                p={3}
              >
                <Flex align="center" gap={2} mb={1} wrap="wrap">
                  <Text fontSize="sm" fontWeight="700" color={bl.cor}>
                    {bl.titulo}
                  </Text>
                  <Badge colorScheme={bl.badge}>
                    {grupos.length === doBloco.length
                      ? `${doBloco.length}`
                      : `${grupos.length} (${doBloco.length} ocorrências)`}
                  </Badge>
                </Flex>
                <Text fontSize="11px" color="#64748B" mb={3}>
                  {bl.sub}
                </Text>
                <VStack align="stretch" spacing={3}>
                  {grupos.map(g => (
                    <AchadoAgrupado key={g.chave} grupo={g} />
                  ))}
                </VStack>
              </Box>
            );
          })}

          {legitimas.length > 0 ? (
            <Box>
              <Text
                as="button"
                fontSize="xs"
                fontWeight="600"
                color="#7C3AED"
                onClick={() => setMostrarLegitimas(x => !x)}
              >
                {mostrarLegitimas ? '▾ Ocultar' : '▸ Mostrar'} {legitimas.length} legítima(s){' '}
                (concordância / atualização de campo — sem infidelidade)
              </Text>
              {mostrarLegitimas ? (
                <VStack align="stretch" spacing={3} mt={2}>
                  {legitimas.map(v => (
                    <VeredictoRow key={`${v.clausula_numero}-${v.classificacao}`} v={v} />
                  ))}
                </VStack>
              ) : null}
            </Box>
          ) : null}
        </VStack>
      )}

      {/* Camada 1 F2 — mapa COMPLETO (origem de cada cláusula). COLAPSADO por
          padrão pra não carregar a tela — o foco é o que a IA apontou acima. */}
      {auditoria.clausulas && auditoria.clausulas.length > 0 ? (
        <Box>
          <Text
            as="button"
            fontSize="xs"
            fontWeight="600"
            color="#7C3AED"
            onClick={() => setMostrarMapa(x => !x)}
          >
            {mostrarMapa ? '▾ Ocultar' : '▸ Ver'} mapa completo — {auditoria.clausulas.length}{' '}
            cláusulas (do vigente vs gerado)
          </Text>
          {mostrarMapa ? (
            <Box mt={2}>
              <MapaClausulas clausulas={auditoria.clausulas} />
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* Fase 1 GATE — confirmação da vistoria (só quando `contratoId` é passado:
          painel pós-geração + drawer da lupa). Ao confirmar, o back destranca o
          download (409 → 200). O relatório em `/auditoria` (feed) não passa
          contratoId → segue read-only. */}
      {contratoId != null &&
        (jaRevisada ? (
          <Flex
            align="center"
            gap={2}
            pt={3}
            borderTopWidth="1px"
            borderColor="rgba(15,23,42,0.08)"
          >
            <CheckCircle size={20} color="#16A34A" weight="fill" />
            <Text fontSize="sm" color="#16A34A" fontWeight="600">
              Vistoria revisada — download liberado.
            </Text>
          </Flex>
        ) : (
          <Flex
            direction="column"
            gap={2}
            align="flex-start"
            pt={3}
            borderTopWidth="1px"
            borderColor="rgba(15,23,42,0.08)"
          >
            <Text fontSize="xs" color="#64748B">
              Revise as divergências acima. Ao confirmar, você assume que conferiu {conferiu} e
              libera o download deste contrato.
            </Text>
            <Button
              colorScheme="green"
              leftIcon={<CheckCircle size={16} weight="bold" />}
              isLoading={confirmar.isPending}
              onClick={() => confirmar.mutate()}
            >
              Conferi a vistoria — liberar download
            </Button>
          </Flex>
        ))}
    </VStack>
  );
};

export default RelatorioFidelidade;
